import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CRM_BATCH_CHOICES } from '../../batch/batchUpdate.queries';
import { SUPPORT_LEVELS } from '../../calls/logCall';
import { ContactDetail, personName } from '../contactDetail';
import { detailsChange, DetailsForm, detailsFormOf, placeWords } from './contactEdit';
import { CRM_UPDATE_CONTACT } from './contactEdit.queries';
import { CountryField } from './CountryField';
import { useContactEdit } from './useContactEdit';

const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
    <Typography component="legend" variant="overline" color="text.secondary" sx={{ lineHeight: 2, mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const grid = (columns: Record<string, string>) => ({ display: 'grid', gap: 1.5, gridTemplateColumns: columns });

/**
 * Name, place and outreach fields of one person (card 21.5), in one form. Only
 * the fields changed are sent, so the timeline logs exactly those. Ctrl+Enter
 * saves. A changed place drops the map point, which was the old place's.
 */
export const EditDetailsDialog = ({ contact, onClose, onSaved }: { contact: ContactDetail; onClose: () => void; onSaved: () => void }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<DetailsForm>(() => detailsFormOf(contact));
  const { data: choices } = useQuery<{ crmAdmins: Array<{ id: number; firstName: string | null; lastName: string | null }> }>(
    CRM_BATCH_CHOICES,
    { fetchPolicy: 'cache-first' },
  );
  const { run, loading, error } = useContactEdit(CRM_UPDATE_CONTACT);

  const change = useMemo(() => detailsChange(form, contact), [form, contact]);
  const changed = Object.keys(change).length;
  const set = (key: keyof DetailsForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));
  const errorOn = (field: string) => error?.field === field;

  const admins = choices?.crmAdmins ?? [];
  // The current point person stays offered while the admin list loads, or if they are no longer an admin.
  const owners = contact.owner && !admins.some((a) => a.id === contact.owner?.id) ? [contact.owner, ...admins] : admins;

  const save = async () => {
    if (!changed || loading) return;
    if ((await run({ id: contact.id, change })).ok) onSaved();
  };

  const text = (key: keyof DetailsForm, label: string, extra: Record<string, unknown> = {}) => (
    <TextField
      label={label}
      value={form[key]}
      onChange={set(key)}
      size="small"
      error={errorOn(key)}
      inputProps={{ maxLength: key === 'stage' ? 50 : 100 }}
      {...extra}
    />
  );

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      fullScreen={phone}
      maxWidth="sm"
      fullWidth
      aria-labelledby="crm-edit-details-title"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          save();
        }
      }}
    >
      <DialogTitle id="crm-edit-details-title">Edit details</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2.5, pt: '8px !important' }}>
        <Group title="Name">
          <Box sx={grid({ xs: '1fr 1fr', sm: '90px 1fr 1fr' })}>
            {text('prefix', 'Title', { placeholder: 'Dr' })}
            {text('firstName', 'First name', { autoFocus: true })}
            {text('middleName', 'Middle name')}
            {text('lastName', 'Last name')}
            {text('suffix', 'Suffix', { placeholder: 'Jr' })}
          </Box>
        </Group>

        <Group title="Place">
          <Box sx={grid({ xs: '1fr', sm: '1fr 1fr 1fr' })}>
            <CountryField value={form.countryCode} onChange={(code) => setForm((f) => ({ ...f, countryCode: code }))} error={errorOn('countryCode')} />
            {text('region', 'Region or state')}
            {text('city', 'Town or city')}
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
            What the contact list filters on{placeWords(form) ? `: ${placeWords(form)}` : ''}. Changing it clears their map point.
          </Typography>
        </Group>

        <Group title="Outreach">
          <Box sx={grid({ xs: '1fr', sm: '1fr 1fr' })}>
            <TextField select label="Point person" value={form.pointPerson} onChange={set('pointPerson')} size="small" error={errorOn('pointPerson')}>
              <MenuItem value="">
                <em>Nobody</em>
              </MenuItem>
              {owners.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {personName(a) || `Admin #${a.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Support level" value={form.supportLevel} onChange={set('supportLevel')} size="small">
              <MenuItem value="">
                <em>Not known</em>
              </MenuItem>
              {SUPPORT_LEVELS.map((level) => (
                <MenuItem key={level.value} value={String(level.value)}>
                  {level.value} · {level.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>{text('stage', 'Stage', { fullWidth: true, placeholder: 'Volunteer' })}</Box>
          </Box>
        </Group>

        {error && <Alert severity="error">{error.message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }} aria-live="polite">
          {changed ? `${changed} ${changed === 1 ? 'change' : 'changes'}, each on their timeline` : 'Nothing changed yet'}
        </Typography>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!changed || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
